// Client-safe credit pack catalogue.
// Pricing model: R0.70 per credit + R5 admin fee per top-up.
// Minimum top-up: R20 of credits (29 credits) → R25 total with admin fee.
// Premium AI CV = 5 credits per generation.

export const PRICE_PER_CREDIT_ZAR = 0.70;
export const ADMIN_FEE_ZAR = 5;
// Minimum spend on credits themselves. With the R5 admin fee this means the
// smallest top-up a customer can place is R30 total (R25 credits + R5 fee).
export const MIN_CREDIT_SPEND_ZAR = 25;
/** Minimum total a customer is charged on a top-up (credits + admin fee). */
export const MIN_TOPUP_TOTAL_ZAR = MIN_CREDIT_SPEND_ZAR + ADMIN_FEE_ZAR;
export const PREMIUM_CV_CREDIT_COST = 5;

export type CreditPack = {
  code: string;
  credits: number;
  /** Total amount charged to the customer (credits + admin fee), in ZAR. */
  amountZar: number;
  /** Portion attributed to credits, in ZAR. */
  creditsValueZar: number;
  /** Admin fee portion, in ZAR. */
  adminFeeZar: number;
  label: string;
  blurb?: string;
};

function buildPack(
  code: string,
  credits: number,
  label: string,
  blurb?: string,
): CreditPack {
  const creditsValueZar = Math.round(credits * PRICE_PER_CREDIT_ZAR * 100) / 100;
  const amountZar = Math.round((creditsValueZar + ADMIN_FEE_ZAR) * 100) / 100;
  return {
    code,
    credits,
    amountZar,
    creditsValueZar,
    adminFeeZar: ADMIN_FEE_ZAR,
    label,
    blurb,
  };
}

export const CREDIT_PACKS: CreditPack[] = [
  buildPack("starter-36",  36,  "Starter",  "Minimum top-up — R30 total"),
  buildPack("popular-72",  72,  "Popular",  "~14 premium CVs or 72 AI tasks"),
  buildPack("value-150",   150, "Value",    "30 premium CVs"),
  buildPack("bulk-300",    300, "Bulk",     "60 premium CVs"),
];

export function findPack(code: string): CreditPack | undefined {
  return CREDIT_PACKS.find((p) => p.code === code);
}

/** Build a custom pack from a credit count (≥ MIN_CREDIT_SPEND_ZAR worth). */
export function buildCustomPack(credits: number): CreditPack {
  const min = Math.ceil(MIN_CREDIT_SPEND_ZAR / PRICE_PER_CREDIT_ZAR);
  const n = Math.max(min, Math.floor(credits));
  return buildPack(`custom-${n}`, n, "Custom", `${n} credits`);
}
